import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { FaArrowLeft } from "react-icons/fa";
import { FiDownload, FiFileText, FiInfo } from "react-icons/fi";

import ActionButton from "../../../../Common/ActionButton";
import ApproverPicker from "../../../../Common/ApproverPicker";
import { useSession } from "../../../../../context/SessionContext";
import { formatAmountWithCommas } from "../../../../../Utils/utils";
import { filterStoreProjects } from "../../../projectFilters";
import { getQuotableRequisitions } from "../Quotes/quotesService";
import {
  getCandidates,
  getComparisonById,
  slotFromQuote,
  saveComparison,
} from "./quotesComparisonService";

const emptySlot = (i) => ({
  slot: i + 1,
  QuoteId: "",
  VendorName: "",
  AttachmentName: "",
  CarriageFreight: 0,
  PaymentTerms: "",
  Awarded: false,
  Rates: {},
});

const AddQuotesComparison = () => {
  const history = useHistory();
  const location = useLocation();
  const { rolesdata } = useSession();

  const populateData = location.state || {};
  const isEdit = Boolean(populateData?.isEdit);

  // Requisitions to pick from and the quotes of the chosen requisition (or of the project, when direct)
  const [requisitions, setRequisitions] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [editData, setEditData] = useState(null);

  // Projects list
  const projectList = useMemo(
    () => filterStoreProjects(rolesdata?.data?.Projects) || [],
    [rolesdata],
  );

  // Form State - Reference Details
  const [formData, setFormData] = useState({
    CSNumber: "",
    ProjectId: "",
    RequisitionId: "",
    RequisitionNo: "",
    CSDate: moment().format("YYYY-MM-DD"),
    Status: "Pending Approval",
    Remarks: "",
  });

  // Requisition Items State
  const [items, setItems] = useState([]);

  // Vendor Slots State (3 columns, more when an existing comparison has more quotes)
  const [vendors, setVendors] = useState([0, 1, 2].map(emptySlot));
  const slotIndexes = vendors.map((_, i) => i);

  const [approverUserIds, setApproverUserIds] = useState([]);
  const [awardSelected, setAwardSelected] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Edit: the list row carries no matrix, so load the comparison itself.
  useEffect(() => {
    if (!isEdit || !populateData.Id) {
      return;
    }
    getComparisonById(populateData.Id)
      .then((c) => {
        if (c.Status !== "Pending Approval" || c.Approvals.some((a) => a.Status !== "Pending")) {
          toast.error("Only a comparison awaiting its first decision can be edited.");
          history.push("/procurement/quotations/comparison");
          return;
        }
        setFormData({
          CSNumber: c.CSNumber,
          ProjectId: String(c.ProjectId),
          RequisitionId: c.RequisitionId ? String(c.RequisitionId) : "",
          RequisitionNo: c.RequisitionNo,
          CSDate: c.CSDate,
          Status: c.Status,
          Remarks: c.Remarks,
        });
        setItems(c.Items);
        const slots = c.Vendors.map((v, i) => ({ ...v, slot: i + 1 }));
        while (slots.length < 3) {
          slots.push(emptySlot(slots.length));
        }
        setVendors(slots);
        setApproverUserIds(c.Approvals.map((a) => a.ApproverUserId));
        setEditData(c);
      })
      .catch((err) => {
        toast.error(err.message || "Failed to load comparison");
        history.push("/procurement/quotations/comparison");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Requisitions follow the project.
  useEffect(() => {
    if (!formData.ProjectId) {
      setRequisitions([]);
      return undefined;
    }
    let cancelled = false;
    getQuotableRequisitions(formData.ProjectId)
      .then((list) => {
        if (!cancelled) {
          setRequisitions(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.message || "Failed to load requisitions");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formData.ProjectId]);

  // Matrix rows + quotes to pick from follow the requisition ("None / Direct" = the project's direct quotes).
  useEffect(() => {
    if (!formData.ProjectId || (isEdit && !editData)) {
      return undefined;
    }
    let cancelled = false;
    getCandidates(formData.RequisitionId, formData.ProjectId)
      .then(({ Items, Quotes }) => {
        if (cancelled) {
          return;
        }
        // Edit: keep rows of the saved comparison that are no longer open on the requisition.
        const extra = (editData?.Items || []).filter((e) => !Items.some((i) => i.id === e.id));
        const merged = [...Items, ...extra];
        setItems(merged);
        setAvailableQuotes(Quotes);
        setVendors((prev) =>
          prev.map((v) => {
            const q = Quotes.find((x) => x.QuoteId === v.QuoteId);
            return q ? slotFromQuote(q, merged, v.slot, v.Awarded) : v;
          }),
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setItems([]);
          setAvailableQuotes([]);
          toast.error(err.message || "Failed to load quotes");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formData.ProjectId, formData.RequisitionId, isEdit, editData]);

  const requisitionOptions = useMemo(() => {
    const list = requisitions.map((r) => ({ value: r.value, label: r.label }));
    if (formData.RequisitionId && !list.some((o) => o.value === formData.RequisitionId)) {
      list.push({ value: formData.RequisitionId, label: formData.RequisitionNo });
    }
    return list;
  }, [requisitions, formData.RequisitionId, formData.RequisitionNo]);

  const resetMatrix = () => {
    setItems([]);
    setAvailableQuotes([]);
    setVendors([0, 1, 2].map(emptySlot));
  };

  // A new project invalidates the requisition, the matrix and the approvers.
  const handleProjectChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      ProjectId: e.target.value,
      RequisitionId: "",
      RequisitionNo: "",
    }));
    setApproverUserIds([]);
    resetMatrix();
  };

  // Handle Linked Requisition Change
  const handleRequisitionChange = (e) => {
    const val = e.target.value;
    const match = requisitions.find((r) => r.value === val);
    setFormData((prev) => ({
      ...prev,
      RequisitionId: val,
      RequisitionNo: match?.requisitionNo || "",
    }));
    resetMatrix();
    if (match) {
      toast.info(`Materials loaded for ${match.requisitionNo}`);
    }
  };

  // Handle picking a quote for a vendor slot
  const handleSelectQuoteForSlot = (slotIndex, quoteId) => {
    if (!quoteId) {
      setVendors((prev) => {
        const next = [...prev];
        next[slotIndex] = emptySlot(slotIndex);
        return next;
      });
      return;
    }

    const quote = availableQuotes.find((q) => q.QuoteId === quoteId);
    if (!quote) {
      return;
    }

    // Check if quote already selected in another slot
    const alreadySelected = vendors.some((v, idx) => idx !== slotIndex && v.QuoteId === quoteId);
    if (alreadySelected) {
      toast.warning("This quote is already selected in another slot");
    }

    setVendors((prev) => {
      const next = [...prev];
      // an expired quote can be shown but not awarded
      const keepAward = Boolean(next[slotIndex]?.Awarded) && !quote.Expired;
      next[slotIndex] = slotFromQuote(quote, items, slotIndex + 1, keepAward);
      return next;
    });

    toast.success(`Loaded "${quote.VendorName}" quotation into Vendor ${slotIndex + 1}`);
  };

  const handleAwardToggle = (slotIndex) => {
    const v = vendors[slotIndex];
    if (v.Expired && !v.Awarded) {
      toast.error(`Quote ${v.QuoteNo} has expired and cannot be awarded`);
      return;
    }
    setVendors((prev) => {
      const next = [...prev];
      next[slotIndex] = {
        ...next[slotIndex],
        Awarded: !next[slotIndex].Awarded,
      };
      return next;
    });
  };

  // Find lowest rate for each item to highlight green
  const lowestRatesPerItem = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const validRates = vendors
        .map((v) => v.Rates?.[item.id])
        .filter((r) => r && r.supplied && Number(r.unitRate) > 0)
        .map((r) => Number(r.unitRate));
      map[item.id] = validRates.length > 0 ? Math.min(...validRates) : null;
    });
    return map;
  }, [items, vendors]);

  // Save Comparison
  const handleSave = async () => {
    if (!formData.ProjectId) {
      toast.error("Please select a Project / Site");
      return;
    }
    if (!formData.CSDate) {
      toast.error("Please specify CS Date");
      return;
    }
    if (items.length === 0) {
      toast.error("There are no materials to compare for this selection");
      return;
    }
    const filledVendors = vendors.filter((v) => v.VendorName);
    if (filledVendors.length < 2) {
      toast.warning("Please configure at least 2 vendors to perform a rate comparison");
    }

    setIsSaving(true);
    try {
      const saved = await saveComparison({
        Id: isEdit ? populateData.Id : "",
        RequisitionId: formData.RequisitionId,
        ProjectId: formData.ProjectId,
        CSDate: formData.CSDate,
        Remarks: formData.Remarks,
        Vendors: vendors,
        Items: items,
        SavedLines: isEdit ? editData?.Lines : [],
        ApproverUserIds: approverUserIds,
      });
      const no = saved.ComparisonNumber || formData.CSNumber;
      toast.success(
        isEdit
          ? `Comparative Statement ${no} updated successfully!`
          : `Comparative Statement ${no} saved successfully!`,
      );
      history.push("/procurement/quotations/comparison");
    } catch (err) {
      toast.error(err.message || "Failed to save comparison");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">{isEdit ? "Edit" : "Add"} Quotation Comparison</h4>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          CARD 1: Reference Details (Matching user reference image)
      ════════════════════════════════════════════════════════════════════ */}
      <div
        className="card mb-4"
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div className="card-header bg-white border-0 pt-3 pb-0">
          <h6 style={{ fontWeight: 700, color: "#1f2937", fontSize: "15px" }}>Reference Details</h6>
        </div>

        <div className="card-body pt-2 pb-3">
          {/* Row 1: CS Number & Project / Site */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="input_label mb-1" style={{ fontWeight: 500, fontSize: "13px" }}>
                CS Number
              </label>
              <input
                type="text"
                className="input_styling w-100"
                style={{ backgroundColor: "#f3f4f6", color: "#4b5563" }}
                value={formData.CSNumber || "Assigned on save"}
                disabled
              />
            </div>

            <div className="col-md-6">
              <div className="d-flex align-items-center mb-1" style={{ gap: "4px" }}>
                <label className="input_label m-0" style={{ fontWeight: 500, fontSize: "13px" }}>
                  Project / Site
                </label>
                <span className="text-danger">*</span>
                <FiInfo
                  size="14"
                  color="#9ca3af"
                  className="ml-auto"
                  title="Select project or construction site"
                />
              </div>
              <select
                className="input_styling w-100"
                value={formData.ProjectId}
                onChange={handleProjectChange}
                disabled={isEdit}
              >
                <option value="">Select Site</option>
                {projectList.map((p, idx) => (
                  <option key={idx} value={String(p.ProjectId)}>
                    {p.ProjectName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Linked Requisition, CS Date, Status */}
          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="input_label mb-1" style={{ fontWeight: 500, fontSize: "13px" }}>
                Linked Requisition
              </label>
              <select
                className="input_styling w-100"
                value={formData.RequisitionId}
                onChange={handleRequisitionChange}
                disabled={isEdit}
              >
                <option value="">None / Direct Quote</option>
                {requisitionOptions.map((r, idx) => (
                  <option key={idx} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                This pulls the materials and quantities automatically
              </small>
            </div>

            <div className="col-md-3 mb-2">
              <label className="input_label mb-1" style={{ fontWeight: 500, fontSize: "13px" }}>
                CS Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="input_styling w-100"
                value={formData.CSDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, CSDate: e.target.value }))}
              />
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                Date picker input, no time
              </small>
            </div>

            <div className="col-md-3 mb-2">
              <label className="input_label mb-1" style={{ fontWeight: 500, fontSize: "13px" }}>
                Status
              </label>
              <div>
                <select className="input_styling w-100" value={formData.Status} disabled>
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          CARD 2: Rate Comparison Matrix (Exact visual match to user image)
      ════════════════════════════════════════════════════════════════════ */}
      <div
        className="card"
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div className="card-header bg-white border-0 pt-3 pb-2 d-flex justify-content-between align-items-center">
          <h6 style={{ fontWeight: 700, color: "#1f2937", fontSize: "15px", margin: 0 }}>
            Rate Comparison Matrix
          </h6>
          <span
            className="badge badge-light border text-muted px-2 py-1"
            style={{ fontSize: "11px" }}
          >
            Compare max 3 quotations side-by-side
          </span>
        </div>

        <div className="card-body p-3">
          <div className="table-responsive border" style={{ borderRadius: "6px" }}>
            <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
              {/* ── Table Header: Vendor Slots with Quote Pickers ── */}
              <thead>
                <tr style={{ backgroundColor: "#F9FAFB" }}>
                  <th
                    style={{
                      width: "25%",
                      verticalAlign: "middle",
                      padding: "12px",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                      <span>Material & Requested Qty.</span>
                      <FiInfo size="14" color="#9ca3af" />
                    </div>
                  </th>

                  {slotIndexes.map((slotIdx) => {
                    const currentVendor = vendors[slotIdx];
                    return (
                      <th
                        key={slotIdx}
                        style={{
                          width: "25%",
                          verticalAlign: "top",
                          padding: "10px 12px",
                          backgroundColor: currentVendor?.Awarded ? "#f0fdf4" : "#F9FAFB",
                          borderLeft: "1px solid #e5e7eb",
                        }}
                      >
                        <div
                          className="d-flex justify-content-between align-items-center mb-1"
                          style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}
                        >
                          <span>Vendor {slotIdx + 1}</span>
                          {currentVendor?.QuoteId && (
                            <span className="badge badge-secondary" style={{ fontSize: "10px" }}>
                              {currentVendor.QuoteNo || "Quote"}
                            </span>
                          )}
                        </div>

                        {/* Dropdown to pick quote/vendor */}
                        <select
                          className="input_styling w-100"
                          style={{
                            fontSize: "12px",
                            padding: "4px 8px",
                            height: "32px",
                            fontWeight: 600,
                          }}
                          value={currentVendor?.QuoteId || ""}
                          onChange={(e) => handleSelectQuoteForSlot(slotIdx, e.target.value)}
                        >
                          <option value="">-- Select from Quotes --</option>
                          {availableQuotes.map((q) => (
                            <option key={q.QuoteId} value={q.QuoteId} disabled={q.Expired}>
                              {q.VendorName} ({q.QuoteNumber}){q.Expired ? " - expired" : ""}
                            </option>
                          ))}
                        </select>

                        {/* Quote PDF attachment badge matching image */}
                        <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top">
                          <div
                            className="d-flex align-items-center"
                            style={{
                              backgroundColor: "#fff",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "11px",
                              gap: "6px",
                              color: "#4b5563",
                              maxWidth: "85%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FiFileText color="#dc2626" size="13" />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                              {currentVendor?.AttachmentUrl
                                ? currentVendor.AttachmentName
                                : "No attachment"}
                            </span>
                          </div>
                          <FiDownload
                            size="13"
                            color="#6b7280"
                            style={{ cursor: "pointer" }}
                            title="Download Quote PDF"
                            onClick={() =>
                              currentVendor?.AttachmentUrl &&
                              window.open(currentVendor.AttachmentUrl, "_blank")
                            }
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* ── Table Body: Material Rows ── */}
              <tbody>
                {items.map((item, rowIdx) => {
                  const lowestRate = lowestRatesPerItem[item.id];

                  return (
                    <tr key={item.id || rowIdx}>
                      <td
                        style={{
                          verticalAlign: "middle",
                          fontWeight: 600,
                          color: "#1f2937",
                          padding: "12px",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        {item.label || `${item.materialName} (${item.qty} ${item.uom})`}
                      </td>

                      {slotIndexes.map((slotIdx) => {
                        const vendor = vendors[slotIdx];
                        const rateData = vendor?.Rates?.[item.id] || {
                          supplied: false,
                          unitRate: 0,
                          total: 0,
                        };
                        const isSupplied = rateData.supplied;
                        const isLowest =
                          isSupplied &&
                          lowestRate !== null &&
                          Number(rateData.unitRate) === lowestRate;

                        return (
                          <td
                            key={slotIdx}
                            style={{
                              verticalAlign: "middle",
                              padding: "8px 12px",
                              backgroundColor: isLowest ? "#dcfce7" : "transparent",
                              transition: "background-color 0.2s",
                            }}
                          >
                            {isSupplied ? (
                              <div
                                className="d-flex align-items-center justify-content-between"
                                style={{ gap: "8px" }}
                              >
                                <div
                                  className="d-flex align-items-center"
                                  style={{ maxWidth: "55%" }}
                                >
                                  <input
                                    type="number"
                                    className="input_styling text-right w-100"
                                    style={{
                                      padding: "3px 6px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      height: "30px",
                                    }}
                                    value={rateData.unitRate || ""}
                                    readOnly
                                  />
                                  <span
                                    className="text-muted ml-1"
                                    style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                                  >
                                    / {item.uom}
                                  </span>
                                </div>

                                <div
                                  style={{
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    color: "#111827",
                                    textAlign: "right",
                                  }}
                                >
                                  {formatAmountWithCommas(
                                    rateData.total || rateData.unitRate * item.qty,
                                  )}
                                  {rateData.remaining != null && (
                                    <div
                                      className="text-muted"
                                      style={{ fontSize: "11px", fontWeight: 400 }}
                                    >
                                      Remaining: {rateData.remaining}
                                    </div>
                                  )}
                                  {rateData.remaining != null && vendor?.Awarded && (
                                    <input
                                      type="number"
                                      min="1"
                                      max={rateData.remaining}
                                      title="Quantity to award"
                                      placeholder="Award qty"
                                      className="input_styling text-right mt-1"
                                      style={{ width: "90px", height: "24px", fontSize: "11px" }}
                                      value={rateData.awardQty ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setVendors((prev) => {
                                          const next = [...prev];
                                          const v = next[slotIdx];
                                          next[slotIdx] = {
                                            ...v,
                                            Rates: {
                                              ...v.Rates,
                                              [item.id]: { ...v.Rates[item.id], awardQty: val },
                                            },
                                          };
                                          return next;
                                        });
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div
                                className="d-flex justify-content-between align-items-center text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                <span style={{ fontStyle: "italic", color: "#6b7280" }}>
                                  -- (Not Supplied / N/A)
                                </span>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {/* ── Carriage / Freight Row ── */}
                <tr>
                  <td
                    style={{
                      verticalAlign: "middle",
                      fontWeight: 600,
                      padding: "12px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    Carriage / Freight (PKR)
                  </td>

                  {slotIndexes.map((slotIdx) => {
                    const vendor = vendors[slotIdx];
                    return (
                      <td key={slotIdx} style={{ verticalAlign: "middle", padding: "8px 12px" }}>
                        <div className="d-flex align-items-center">
                          <input
                            type="number"
                            className="input_styling w-100 text-right"
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px",
                              fontWeight: 500,
                              height: "30px",
                            }}
                            value={vendor?.CarriageFreight || ""}
                            placeholder="0"
                            readOnly
                          />
                          {vendor?.CarriageNote && (
                            <span
                              className="ml-1 text-muted"
                              style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                            >
                              ({vendor.CarriageNote})
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* ── Payment & Credit Terms Row ── */}
                <tr>
                  <td
                    style={{
                      verticalAlign: "middle",
                      fontWeight: 600,
                      padding: "12px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    Payment & Credit Terms
                  </td>

                  {slotIndexes.map((slotIdx) => {
                    const vendor = vendors[slotIdx];
                    return (
                      <td key={slotIdx} style={{ verticalAlign: "middle", padding: "8px 12px" }}>
                        <input
                          type="text"
                          className="input_styling w-100"
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#056363",
                            height: "30px",
                          }}
                          value={vendor?.PaymentTerms || ""}
                          placeholder="e.g. 15 Days Credit"
                          readOnly
                        />
                      </td>
                    );
                  })}
                </tr>

                {/* ── Award Bidder (Action Checks) Row ── */}
                <tr style={{ backgroundColor: "#F9FAFB" }}>
                  <td style={{ verticalAlign: "middle", fontWeight: 600, padding: "12px" }}>
                    Award Bidder (Action Checks)
                  </td>

                  {slotIndexes.map((slotIdx) => {
                    const vendor = vendors[slotIdx];
                    const isAwarded = Boolean(vendor?.Awarded);

                    return (
                      <td
                        key={slotIdx}
                        style={{
                          verticalAlign: "middle",
                          padding: "10px 12px",
                          backgroundColor: isAwarded ? "#dcfce7" : "transparent",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => handleAwardToggle(slotIdx)}
                      >
                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                          <input
                            type="checkbox"
                            checked={isAwarded}
                            onChange={() => handleAwardToggle(slotIdx)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span
                            style={{
                              fontWeight: isAwarded ? 700 : 500,
                              color: isAwarded ? "#166534" : "#4b5563",
                              fontSize: "13px",
                            }}
                          >
                            Award
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Award Selected Checkbox & Recommendation Notes ── */}
          <div className="d-flex align-items-center justify-content-between mt-3 pt-2">
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <input
                type="checkbox"
                id="awardSelectedCheck"
                checked={awardSelected}
                onChange={(e) => setAwardSelected(e.target.checked)}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              <label
                htmlFor="awardSelectedCheck"
                className="m-0"
                style={{ fontWeight: 600, color: "#166534", fontSize: "14px", cursor: "pointer" }}
              >
                Award Selected
              </label>
            </div>

            <div className="text-muted" style={{ fontSize: "12px" }}>
              Total Awarded Vendors:{" "}
              <strong style={{ color: "#166534" }}>
                {vendors
                  .filter((v) => v.Awarded)
                  .map((v) => v.VendorName)
                  .filter(Boolean)
                  .join(", ") || "None"}
              </strong>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="mt-3">
            <label className="input_label mb-1" style={{ fontSize: "12px" }}>
              Procurement Incharge / Evaluation Remarks:
            </label>
            <textarea
              className="input_styling w-100"
              rows={2}
              style={{ fontSize: "13px", height: "60px" }}
              placeholder="Enter reasons for award, price negotiations, or technical compliance remarks..."
              value={formData.Remarks}
              onChange={(e) => setFormData((prev) => ({ ...prev, Remarks: e.target.value }))}
            />
          </div>

          {/* Approvers (required by the backend) */}
          <div className="mt-3">
            <label className="input_label mb-1" style={{ fontSize: "12px" }}>
              Approver Users:
            </label>
            <ApproverPicker
              projectId={formData.ProjectId}
              value={approverUserIds}
              onChange={setApproverUserIds}
            />
          </div>

          {/* ── Submit Action Buttons ── */}
          <div className="float-right mt-4 mr-md-3 mb-4" style={{ display: "flex", gap: "10px" }}>
            <ActionButton
              text="Cancel"
              variant="cancel"
              onClick={() => history.push("/procurement/quotations/comparison")}
            />
            <ActionButton
              text={isSaving ? "Saving..." : isEdit ? "Update" : "Save"}
              onClick={handleSave}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuotesComparison;
